import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { campaignsApi, DemoUser, DemoFeedbackMetrics } from '../services/api';
import Swal from 'sweetalert2';
import { LeadsDashboard } from './LeadsDashboard';

// Since I don't want to create too many new CSS files if not needed, 
// I'll use some inline styles for the "card" grid and then regular classes.

export default function Funnels() {
  const { getIdToken } = useAuth();
  const [showDemoCampaign, setShowDemoCampaign] = useState(false);
  const [showDabblerLeads, setShowDabblerLeads] = useState(false);
  const [showSomLeads, setShowSomLeads] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<DemoUser[]>([]);
  const [metrics, setMetrics] = useState<DemoFeedbackMetrics | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Selection state
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Compose drawer state
  const [showCompose, setShowCompose] = useState(false);
  const [composeData, setComposeData] = useState({
    sender: 'charlene@mg.charsoft.com',
    subject: 'Thank you for testing CCI Connect AI',
    body: 'Hi {{name}},\n\nThanks again for trying CCI Connect AI in demo mode. You still have access for a bit, so we’d love your thoughts. Would you share feedback here?\n{{surveyLink}}\n\n— Charlene',
    surveyLink: 'https://forms.gle/demo-feedback-example',
  });

  const SENDER_DOMAINS = [
    { label: 'mg.charsoft.com', value: 'charlene@mg.charsoft.com' },
    { label: 'mg.charsoft.ai', value: 'charlene@mg.charsoft.ai' },
    { label: 'mg.cslleesburg.org', value: 'info@mg.cslleesburg.org' },
  ];

  useEffect(() => {
    if (showDemoCampaign) {
      loadDemoUsers();
    }
  }, [showDemoCampaign, statusFilter, page, searchTerm]);

  async function loadDemoUsers() {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) return;

      const response = await campaignsApi.listDemoUsers(token, {
        page,
        status: statusFilter,
        search: searchTerm,
      });

      setUsers(response.items);
      setMetrics(response.metrics);
      setTotalPages(response.pagination.totalPages);
      // Clear selection when list changes
      setSelectedUserIds([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load demo users');
    } finally {
      setLoading(false);
    }
  }

  function toggleSelection(userId: string) {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  }

  function toggleSelectAll() {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(u => u.id));
    }
  }

  async function handleExportCSV() {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) return;

      // Fetch ALL matching users for this filter
      const response = await campaignsApi.listDemoUsers(token, {
        pageSize: 'all' as any, // Cast to any to bypass current type if needed
        status: statusFilter,
        search: searchTerm,
      });

      const allUsers = response.items;
      if (allUsers.length === 0) return;

      // Define CSV headers
      const headers = ['Name', 'Email', 'Signup Date', 'Status', 'Email Sent At', 'Email Opened At', 'Subject'];
      
      // Map user data to rows
      const rows = allUsers.map(user => [
        user.name || '',
        user.email || '',
        user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '',
        user.feedbackEmailStatus || 'not_sent',
        user.feedbackEmailSentAt ? new Date(user.feedbackEmailSentAt).toLocaleString() : '',
        user.feedbackEmailOpenedAt ? new Date(user.feedbackEmailOpenedAt).toLocaleString() : '',
        user.feedbackEmailSubject || ''
      ]);

      // Format as CSV string
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `cci-demo-campaign-status-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'Failed to fetch all data for export.'
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleSendEmail() {
    if (selectedUserIds.length === 0) return;
    setShowCompose(true);
  }

  async function confirmSend() {
    if (selectedUserIds.length === 0) return;
    
    try {
      const token = await getIdToken();
      if (!token) return;

      setLoading(true);
      
      const response = await campaignsApi.sendBulkFeedbackEmail(token, selectedUserIds, composeData);
      
      setShowCompose(false);
      setSelectedUserIds([]);
      
      Swal.fire({
        icon: 'success',
        title: 'Campaign Started',
        text: `Sent ${response.sent} emails successfully. ${response.failed} failed.`,
      });
      
      // Refresh metrics and list
      loadDemoUsers();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Batch Failed',
        text: err instanceof Error ? err.message : 'Failed to process batch',
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleSyncStats() {
    try {
      const token = await getIdToken();
      if (!token) return;

      setLoading(true);
      const response = await campaignsApi.syncCampaignStats(token);
      
      Swal.fire({
        icon: 'success',
        title: 'Stats Synced',
        text: `Successfully synced ${response.updated} historical opens from Mailgun.`,
        timer: 3000,
      });
      
      loadDemoUsers();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Sync Failed',
        text: err instanceof Error ? err.message : 'Failed to sync stats',
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleSendTest() {
    try {
      const token = await getIdToken();
      if (!token) return;

      setLoading(true);
      await campaignsApi.sendTestFeedbackEmail(token, composeData);
      
      Swal.fire({
        icon: 'success',
        title: 'Test Email Sent',
        text: 'A test email has been sent to charlene@charsoft.com',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Test Failed',
        text: err instanceof Error ? err.message : 'Failed to send test email',
      });
    } finally {
      setLoading(false);
    }
  }

  if (showDabblerLeads) {
    return (
      <div style={{ padding: '2rem' }}>
        <button 
          onClick={() => setShowDabblerLeads(false)}
          style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', marginBottom: '1rem', padding: 0 }}
        >
          ← Back to Funnels
        </button>
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <LeadsDashboard filterProject="dabbler" />
        </div>
      </div>
    );
  }

  if (showSomLeads) {
    return (
      <div style={{ padding: '2rem' }}>
        <button 
          onClick={() => setShowSomLeads(false)}
          style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', marginBottom: '1rem', padding: 0 }}
        >
          ← Back to Funnels
        </button>
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <LeadsDashboard filterProject="scienceofmind-ai" />
        </div>
      </div>
    );
  }

  if (showDemoCampaign) {
    return (
      <div style={{ padding: '2rem' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <button 
              onClick={() => setShowDemoCampaign(false)}
              style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', marginBottom: '0.5rem', padding: 0 }}
            >
              ← Back to Funnels
            </button>
            <h1 style={{ margin: 0 }}>Demo Feedback Campaign</h1>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              onClick={handleExportCSV}
              disabled={users.length === 0}
              style={{ 
                padding: '0.75rem 1.5rem', 
                background: 'white', 
                color: '#64748b', 
                border: '1px solid #cbd5e1', 
                borderRadius: '8px', 
                fontWeight: 600,
                cursor: users.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              Export CSV
            </button>

            <button 
              onClick={handleSyncStats}
              disabled={loading}
              style={{ 
                padding: '0.75rem 1.5rem', 
                background: 'white', 
                color: '#3b82f6', 
                border: '1px solid #3b82f6', 
                borderRadius: '8px', 
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Syncing...' : 'Sync with Mailgun'}
            </button>

            {selectedUserIds.length > 0 && (
              <button 
                onClick={handleSendEmail}
                style={{ 
                  padding: '0.75rem 1.5rem', 
                  background: '#3b82f6', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Send to {selectedUserIds.length} Selected
              </button>
            )}
          </div>
        </header>

        {error && (
          <div style={{ padding: '1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '2rem' }}>
            {error}
          </div>
        )}

        {metrics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Not Sent</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{metrics.not_sent}</div>
            </div>
            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Sent</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{metrics.sent}</div>
            </div>
            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Opened</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#22c55e' }}>{metrics.opened}</div>
            </div>
            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Failed</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>{metrics.failed}</div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <input 
            type="text" 
            placeholder="Search name or email..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', flex: 1, minWidth: '200px' }}
          />
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
          >
            <option value="all">All Statuses</option>
            <option value="not_sent">Not Sent</option>
            <option value="sent">Sent</option>
            <option value="opened">Opened</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div style={{ overflowX: 'auto', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead style={{ background: '#f1f5f9' }}>
              <tr>
                <th style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0', width: '40px' }}>
                  <input 
                    type="checkbox" 
                    checked={users.length > 0 && selectedUserIds.length === users.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>Name</th>
                <th style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>Email</th>
                <th style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>Signup</th>
                <th style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                <th style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>Last Sent</th>
                <th style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '1rem' }}>
                    <input 
                      type="checkbox" 
                      checked={selectedUserIds.includes(user.id)}
                      onChange={() => toggleSelection(user.id)}
                    />
                  </td>
                  <td style={{ padding: '1rem' }}>{user.name || '—'}</td>
                  <td style={{ padding: '1rem' }}>{user.email}</td>
                  <td style={{ padding: '1rem' }}>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ 
                      padding: '0.25rem 0.5rem', 
                      borderRadius: '9999px', 
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: 
                        user.feedbackEmailStatus === 'opened' ? '#dcfce7' :
                        user.feedbackEmailStatus === 'sent' ? '#dbeafe' :
                        user.feedbackEmailStatus === 'failed' ? '#fee2e2' : '#f1f5f9',
                      color:
                        user.feedbackEmailStatus === 'opened' ? '#166534' :
                        user.feedbackEmailStatus === 'sent' ? '#1e40af' :
                        user.feedbackEmailStatus === 'failed' ? '#991b1b' : '#475569',
                    }}>
                      {user.feedbackEmailStatus || 'not_sent'}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {user.feedbackEmailSentAt ? new Date(user.feedbackEmailSentAt).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => {
                        setSelectedUserIds([user.id]);
                        setShowCompose(true);
                      }}
                      disabled={loading}
                      style={{ 
                        padding: '0.25rem 0.75rem', 
                        background: '#3b82f6', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1
                      }}
                    >
                      Send Email
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>No demo users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          <button 
            disabled={page === 1} 
            onClick={() => setPage(p => p - 1)}
            style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '4px', background: 'white' }}
          >
            Prev
          </button>
          <span style={{ padding: '0.25rem' }}>Page {page} of {totalPages}</span>
          <button 
            disabled={page === totalPages} 
            onClick={() => setPage(p => p + 1)}
            style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '4px', background: 'white' }}
          >
            Next
          </button>
        </div>

        {/* Compose Drawer (Modal-ish) */}
        {showCompose && selectedUserIds.length > 0 && (
          <div style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 
          }}>
            <div style={{ 
              width: '100%', maxWidth: '500px', background: 'white', height: '100%', 
              padding: '2rem', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 15px rgba(0,0,0,0.1)' 
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Compose Feedback</h2>
                <button onClick={() => setShowCompose(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Recipient(s)</label>
                <div style={{ padding: '0.5rem', background: '#f1f5f9', borderRadius: '4px', maxHeight: '100px', overflowY: 'auto' }}>
                  {selectedUserIds.length === 1 
                    ? users.find(u => u.id === selectedUserIds[0])?.email 
                    : `${selectedUserIds.length} users selected`}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Sender Domain</label>
                <select 
                  value={composeData.sender}
                  onChange={(e) => setComposeData(prev => ({ ...prev, sender: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                >
                  {SENDER_DOMAINS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Subject</label>
                <input 
                  type="text"
                  value={composeData.subject}
                  onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ marginBottom: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Body (Markdown supported)</label>
                <textarea 
                  value={composeData.body}
                  onChange={(e) => setComposeData(prev => ({ ...prev, body: e.target.value }))}
                  style={{ width: '100%', flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontFamily: 'monospace' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Survey Link</label>
                <input 
                  type="text"
                  value={composeData.surveyLink}
                  onChange={(e) => setComposeData(prev => ({ ...prev, surveyLink: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <button 
                onClick={handleSendTest}
                disabled={loading || !composeData.body || !composeData.surveyLink}
                style={{ 
                  width: '100%', padding: '0.75rem', background: 'white', color: '#3b82f6', 
                  border: '1px solid #3b82f6', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600,
                  marginBottom: '0.75rem',
                  cursor: (loading || !composeData.body || !composeData.surveyLink) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !composeData.body || !composeData.surveyLink) ? 0.7 : 1
                }}
              >
                Send Test Email to Me
              </button>

              <button 
                onClick={confirmSend}
                disabled={loading || !composeData.body || !composeData.surveyLink}
                style={{ 
                  width: '100%', padding: '1rem', background: '#3b82f6', color: 'white', 
                  border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 600,
                  cursor: (loading || !composeData.body || !composeData.surveyLink) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !composeData.body || !composeData.surveyLink) ? 0.7 : 1
                }}
              >
                {loading ? 'Processing Batch...' : `Send to ${selectedUserIds.length} Users`}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <h1>Funnels</h1>
      <p style={{ color: '#64748b', marginBottom: '2rem' }}>Campaign management and outreach funnels</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '2rem' }}>
        <div 
          onClick={() => setShowDemoCampaign(true)}
          style={{ 
            background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', 
            cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Demo Feedback Campaign</h2>
            <span style={{ padding: '0.25rem 0.5rem', background: '#dbeafe', color: '#1e40af', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>ACTIVE</span>
          </div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            Outreach to CCI Connect AI demo users to gather feedback and convert to pilots.
          </p>
          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#94a3b8' }}>
            <span>Project: cci-connect-ai</span>
            <span>•</span>
            <span>Target: Demo Users</span>
          </div>
          </div>

          {/* Dabbler Leads Funnel Card */}
          <div 
            onClick={() => setShowDabblerLeads(true)}
            style={{ 
              background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', 
              cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Dabbler Leads Funnel</h2>
              <span style={{ padding: '0.25rem 0.5rem', background: '#dcfce7', color: '#16a34a', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>NEW</span>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Convert LinkedIn keyword responders into qualified cohort applications.
            </p>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#94a3b8' }}>
              <span>Project: from-dabbler</span>
              <span>•</span>
              <span>Target: Keyword Leads</span>
            </div>
          </div>

          {/* Science of Mind AI Funnel Card */}
          <div 
            onClick={() => setShowSomLeads(true)}
            style={{ 
              background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', 
              cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#042f2e' }}>Science of Mind AI Funnel</h2>
              <span style={{ padding: '0.25rem 0.5rem', background: '#fef3c7', color: '#d97706', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>ACTIVE</span>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Review "velvet rope" intake leads from Reno Convention.
            </p>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#94a3b8' }}>
              <span>Project: scienceofmind-ai</span>
              <span>•</span>
              <span>Target: Ministry Leaders</span>
            </div>
          </div>

          {/* Placeholder for future funnels */}
        <div style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>+</div>
            <div>New funnel placeholder</div>
          </div>
        </div>
      </div>
    </div>
  );
}

